import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import _sumBy from 'lodash/sumBy';
import Column from 'components/shared/Column';
import MarketId from 'components/shared/MarketId';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import ContextMenu from 'components/shared/ContextMenu';
import Card from 'components/trade/Card';
import ModalCancelOrder from 'components/modals/CancelOrder';
import { formatAmount, formatAsset } from '@nbx/frontend-helpers/format';
import { orderIsNotCancellable } from 'helpers/orders';
import MarketInformationText from 'components/shared/MarketInformationText';
import styles from './styles.module.scss';

const columnWidths = [3, 3.5, 4, 1.5];
const endAlignIndex = 1;

function MobileOrders(props) {
  const microapps = window.MICROFRONTENDS_CONFIG;
  const { t } = useTranslation();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);

  const headers = [t('common_asset'), t('common_price'), t('market_size_filled'), ''];
  const renderLink = useCallback(
    order => `${microapps.routes.orders.path}/${order.id}`,
    [microapps.routes.orders.path]
  );

  const openCancelModal = order => {
    setCancellingOrder(order);
    setCancelModalOpen(true);
  };

  const closeCancelModal = order => {
    props.openOrders?.refetch();
    setCancelModalOpen(false);
    if (!order) {
      setCancellingOrder(null);
    }
  };

  useEffect(() => {
    if (
      cancellingOrder !== null &&
      !props.openOrders.refreshing &&
      !generateKeyIndex(props.openOrders.data).includes(cancellingOrder.id)
    ) {
      setCancellingOrder(null);
    }
  }, [cancellingOrder, props.openOrders.data, props.openOrders.refreshing, props.openOrders.keyIndex]);

  const generateKeyIndex = data => {
    return data.map(datum => datum.id);
  };

  const loading = props.openOrders.loading || props.markets.loading || props.assets.loading;
  const error = props.openOrders.error || props.markets.error || props.assets.error;
  const cancelling = cancellingOrder !== null && !cancelModalOpen;

  const renderItem = useCallback(
    (order, index) => {
      if (index === 2) {
        const isPending = orderIsNotCancellable(order.events);
        return (
          <Column endAligned>
            <Text monospaced styles={[styles.orderTextMobile]}>
              {formatAsset(order.quantity, props.markets.data.markets.baseAsset, props.assets.data)}
            </Text>
            {isPending ? (
              <Text monospaced muted styles={[styles.orderDetailMobile]}>
                {t('common_pending')}
              </Text>
            ) : (
              <Text monospaced muted styles={[styles.orderDetailMobile]}>
                {formatAsset(
                  _sumBy(order.fills, f => +f.quantity),
                  props.markets.data.markets.baseAsset,
                  props.assets.data
                )}
              </Text>
            )}
          </Column>
        );
      }
      if (index === 3) {
        if (orderIsNotCancellable(order.events)) return null;
        return (
          <ContextMenu
            appearsVertical="below"
            appearsHorizontal="left"
            options={[
              {
                label: t('common_cancel_order'),
                onClick: () => openCancelModal(order)
              }
            ]}
          />
        );
      }
      return {
        0: (
          <Column>
            {/* <Text startAligned styles={[styles.orderTextMobile]}>{props.pair}</Text> */}
            <MarketId pair={props.pair} noIcon styles={[styles.orderTextMobile]} />
            <Text
              styles={[
                styles.orderDetailMobile,
                { [styles.buy]: order.side === 'BUY', [styles.sell]: order.side === 'SELL' }
              ]}>
              {order.side === 'BUY' ? t('common_buy') : t('common_sell')}
            </Text>
          </Column>
        ),
        1: (
          <Text monospaced styles={[styles.orderTextMobile]}>
            {order.execution.type === 'MARKET'
              ? t('common_market')
              : formatAmount(order.execution.price, props.markets.data.markets.minTickLength)}
          </Text>
        )
      }[index];
    },
    [
      props.pair,
      props.markets.data.markets.minTickLength,
      props.markets.data.markets.baseAsset,
      props.assets.data,
      t
    ]
  );

  if (props.markets.data?.markets?.disabled) {
    return <MarketInformationText text={t('market_is_disabled', { pair: props.pair })} />;
  }

  return (
    <Card loading={loading} error={error} isMobile>
      <Table
        columnWidths={columnWidths}
        data={props.openOrders.data}
        keyField="id"
        hasNextPage={props.openOrders.hasNextPage && !props.openOrders.pausedForEmptyPages}
        pausedForEmptyPages={props.openOrders.pausedForEmptyPages}
        isLoading={loading || cancelling}
        fetchNextPage={props.openOrders.fetchNextPage}
        fetchAllPages={props.openOrders.fetchAllPages}
        isLoadingNextPage={props.openOrders.loadingNextPage}
        isLoadingAllPages={props.openOrders.loadingAllPages}
        setRefreshEndIndex={props.openOrders.setRefreshEndIndex}
        emptyTitle={t('market_you_dont_have_open_orders')}
        emptyMessage={t('market_as_you_create')}
        endAlignIndex={endAlignIndex}
        headers={headers}
        renderItem={renderItem}
        renderLink={renderLink}
        borders={false}
        rowHeight={55}
        tableStyles={[styles.table]}
        headerStyles={[styles.headers]}
        rowStyles={[styles.row]}
        cellStyles={[styles.cell]}
        hasActions // hasAction prop always assumes that the last column is clickable
      />
      {cancelModalOpen && (
        <ModalCancelOrder
          marketId={props.pair}
          handleClose={closeCancelModal}
          order={cancellingOrder}
          isMobile
        />
      )}
    </Card>
  );
}

MobileOrders.propTypes = {
  pair: PropTypes.string.isRequired,
  openOrders: PropTypes.object,
  assets: PropTypes.object,
  markets: PropTypes.object
};

MobileOrders.defaultProps = {
  openOrders: {},
  markets: {},
  assets: {}
};

export default MobileOrders;
